// Generated from scripts/sticky-add-to-cart.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { ProductFormStateEvent } from '@theme/product-form';
import { StandardEvents } from '@shopify/events';
/** How long the bar shows the form's message in place of the product's name before it shows the name again. */
const MESSAGE_DURATION = 6000;
function toInteger(value) {
    if (value === null || value === undefined || value === '')
        return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
function isFormState(event) {
    return event instanceof CustomEvent && isRecord(event.detail) && typeof event.detail.phase === 'string';
}
function isProductForm(element) {
    return element instanceof HTMLElement && 'add' in element && typeof element.add === 'function';
}
function isAddToCart(element) {
    return element instanceof HTMLElement && 'setPhase' in element && typeof element.setPhase === 'function';
}
/**
 * The sticky add to cart bar: a derived surface of the page's primary product form. The server renders it from the
 * same selected variant as the buy buttons (name, variant, price, availability, picture) and the bar takes the
 * section Shopify renders for each selection, at the same moment the form takes its own pieces, so the two never
 * disagree. It adds through the form (`ProductFormComponent.add`), which owns the checks, the quantity and the cart
 * events, and mirrors the form's state: the same adding and added looks on its button, the form's message in place of
 * the product's name for a few seconds, and the quantity the form will add.
 *
 * The bar shows while the buy buttons are above the viewport and not at the end of the page, and never while the
 * Shopify chat is up. `data-stuck` on the bar and `data-variant-available` on this element are what the edge
 * stacking contract reads (styles/edge-stack.css); a variant that cannot be bought hides the bar.
 *
 * Contract kept from the inherited module: the `sticky-add-to-cart` element, the `stickyBar` ref, `data-stuck`,
 * `data-variant-available`, `data-product-id` and `handleAddToCartClick`. The `puppet` flag and the click on the
 * form's button retired: the bar calls the form.
 */
class StickyAddToCartComponent extends Component {
    requiredRefs = ['stickyBar'];
    #lifetime;
    #observers = [];
    #isStuck = false;
    #buyButtonsAbove = false;
    #footerInView = false;
    #quantity = 1;
    #state = { phase: 'idle', error: undefined };
    #messageTimer = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.#quantity = toInteger(this.dataset.initialQuantity) ?? 1;
        this.#observeVisibility();
        const section = this.closest('.shopify-section');
        section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
        section?.addEventListener(ProductFormStateEvent.eventName, this.#onFormState, { signal });
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
        this.addEventListener('click', this.#onClick, { signal });
        // The chat's button only paints once its app has upgraded <shopify-chat>, after the visibility observers have run.
        void customElements.whenDefined('shopify-chat').then(() => {
            if (!signal.aborted)
                this.#updateVisibility();
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        for (const observer of this.#observers)
            observer.disconnect();
        this.#observers = [];
        window.clearTimeout(this.#messageTimer);
    }
    /** The primary product form of this product: the one the buy buttons belong to. */
    get #form() {
        const { productId } = this.dataset;
        const section = this.closest('.shopify-section');
        if (!productId || !section)
            return undefined;
        const form = section.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
        return isProductForm(form) ? form : undefined;
    }
    /** Adds through the primary form, with the bar's own picture as the start of the motion to the cart. */
    handleAddToCartClick = async () => {
        const form = this.#form;
        if (!form)
            return;
        const image = this.querySelector('img');
        await form.add(image ? { origin: { element: image, image: image.currentSrc || image.src } } : {});
    };
    #onClick = (event) => {
        const button = event.target instanceof Element ? event.target.closest('button') : null;
        if (!button || !this.contains(button) || button.disabled)
            return;
        void this.handleAddToCartClick();
    };
    /* ----- when the bar shows ----- */
    /** Watches the buy buttons (the bar shows once they are above the viewport) and the footer (it hides there). */
    #observeVisibility() {
        const buyButtons = this.#form?.closest('.buy-buttons-block');
        // Themes migrated from 2.0 have no footer element.
        const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
        if (!buyButtons || !footer)
            return;
        const watch = (target, onChange) => {
            const observer = new IntersectionObserver((entries) => {
                const [entry] = entries;
                if (entry)
                    onChange(entry);
                this.#updateVisibility();
            });
            observer.observe(target);
            this.#observers.push(observer);
        };
        // Below the viewport the buy buttons have not been reached yet, so only a block above it counts as scrolled past.
        watch(buyButtons, (entry) => {
            this.#buyButtonsAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        });
        watch(footer, (entry) => {
            this.#footerInView = entry.isIntersecting;
        });
    }
    /** The bar shows while the buy buttons are behind the shopper, the footer is not in view and no chat is up. */
    #updateVisibility() {
        const show = this.#buyButtonsAbove && !this.#footerInView && !this.#isChatActive();
        if (show === this.#isStuck)
            return;
        this.#isStuck = show;
        const { stickyBar } = this.refs;
        if (stickyBar instanceof HTMLElement)
            stickyBar.dataset.stuck = String(show);
    }
    /** The chat's button sits where the bar does, so the bar stays away while the chat is on the page. */
    #isChatActive() {
        return customElements.get('shopify-chat') !== undefined && document.querySelector('shopify-chat') !== null;
    }
    /* ----- what the bar mirrors ----- */
    /** Takes the bar's content from the section Shopify rendered for the selection, as the form takes its own pieces. */
    #onProductSelect = (event) => {
        if (!isProductSelect(event) || !(event.target instanceof Element) || event.target.closest('product-card'))
            return;
        event.promise
            .then(({ detail }) => {
            if (!detail?.html)
                return;
            const { html, productId, resource } = detail;
            if (productId && productId !== this.dataset.productId)
                return;
            // The server renders the first available variant for a selection no variant exists for, so availability
            // comes from the selection, not from the markup.
            const available = resource !== null && resource.available !== false;
            this.dataset.variantAvailable = String(available);
            if (resource?.id !== undefined)
                this.dataset.currentVariantId = String(resource.id);
            const bar = this.refs.stickyBar;
            const next = html.querySelector('sticky-add-to-cart [ref="stickyBar"]');
            // A bar that is hidden has nothing to show; the next selection that can be bought brings its content.
            if (available && bar instanceof HTMLElement && next)
                morph(bar, next);
            this.#render();
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[sticky-add-to-cart] The variant change failed:', error);
        });
    };
    #onFormState = (event) => {
        if (!isFormState(event) || event.target !== this.#form)
            return;
        const messageChanged = event.detail.error !== this.#state.error;
        this.#state = event.detail;
        this.#render();
        // The message shows when the form raises it, not again each time the bar re-renders.
        if (messageChanged)
            this.#showMessage(this.#state.error);
    };
    #onQuantityUpdate = (event) => {
        const form = this.#form;
        if (!form || !(event.target instanceof Node) || !form.contains(event.target))
            return;
        const detail = 'detail' in event ? event.detail : undefined;
        if (!isRecord(detail) || typeof detail.quantity !== 'number')
            return;
        this.#quantity = detail.quantity;
        this.#render();
    };
    /** Projects the form's phase and the quantity onto the bar, which a render of the section resets. */
    #render() {
        const button = this.querySelector('add-to-cart-component');
        if (isAddToCart(button))
            button.setPhase(this.#state.phase);
        const chip = this.querySelector('[data-sticky-quantity]');
        const number = chip?.querySelector('[data-quantity-number]');
        if (chip && number) {
            number.textContent = String(this.#quantity);
            chip.hidden = this.#quantity <= 1;
        }
    }
    /** Shows the form's message in place of the product's name for a few seconds; the form keeps it until the next try. */
    #showMessage(message) {
        const slot = this.querySelector('[data-sticky-message]');
        const info = this.querySelector('[data-sticky-info]');
        if (!slot || !info)
            return;
        window.clearTimeout(this.#messageTimer);
        slot.textContent = message ?? '';
        slot.hidden = !message;
        info.hidden = Boolean(message);
        if (message) {
            this.#messageTimer = window.setTimeout(() => {
                slot.hidden = true;
                info.hidden = false;
            }, MESSAGE_DURATION);
        }
    }
}
if (!customElements.get('sticky-add-to-cart')) {
    customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
